/**
 * Enter task TYPES — ported from Platform's `task-types.ts` (rev 2, 2026-08-20).
 *
 * WHY THIS REPLACES SEVERITY AS THE ORGANISING AXIS: our worklist filtered by
 * severity, derived from Enter's `MessagePriority`. Platform's redesign drops
 * that idea entirely and organises by task TYPE — Halts, Denials, Rejections,
 * Records, Patient info, Authorizations — because that is the axis a biller
 * actually works by. Priority tells you how loud a task is; type tells you what
 * you have to DO, and the two resolve paths differ per type.
 *
 * The matchers are a contract with Enter's message-category catalog. They are
 * copied verbatim from Platform and matched case-insensitively by substring,
 * so "Claim Halted - Coding" still lands under Halts.
 */

import { categoryMatches } from './enter-task-reason';

export type TaskTypeId =
  | 'all'
  | 'halt'
  | 'denial'
  | 'rejection'
  | 'records'
  | 'patient'
  | 'auth'
  | 'done';

/** Group key for the All-types view. Unmatched categories fall to 'other'. */
export type TaskGroupId =
  | 'halt'
  | 'denial'
  | 'rejection'
  | 'records'
  | 'patient'
  | 'auth'
  | 'other';

export interface TaskTypeDefinition {
  id: TaskTypeId;
  /** Chip label in the type filter bar. */
  label: string;
  /** Group header label in the All-types view. */
  groupLabel?: string;
  /**
   * `false` for Done, which lists CLOSED messages (`filterOpen=0`). Every other
   * type lists open work.
   */
  open: boolean;
  /** Category-name matchers; empty means "no category filter". */
  categoryMatchers: readonly string[];
}

export const TASK_TYPE_DEFINITIONS: readonly TaskTypeDefinition[] = [
  { id: 'all', label: 'All types', open: true, categoryMatchers: [] },
  {
    id: 'halt',
    label: 'Halts',
    groupLabel: 'Halts',
    open: true,
    categoryMatchers: [
      'Claim Halted',
      'Invalid Claim - Interface',
      'Invalid Claim - Billing',
      'Invalid Claim - Clinical',
    ],
  },
  {
    id: 'denial',
    label: 'Denials',
    groupLabel: 'Denials',
    open: true,
    categoryMatchers: ['Claim Denied - Rules Matched'],
  },
  {
    id: 'rejection',
    label: 'Rejections',
    groupLabel: 'Rejections',
    open: true,
    categoryMatchers: ['Claim Rejected - Rules Matched'],
  },
  {
    id: 'records',
    label: 'Records',
    groupLabel: 'Records',
    open: true,
    categoryMatchers: ['Missing Medical Records'],
  },
  {
    id: 'patient',
    label: 'Patient info',
    groupLabel: 'Patient info',
    open: true,
    categoryMatchers: ['Inactive/Invalid Subscriber', 'Payers Swapped'],
  },
  {
    id: 'auth',
    label: 'Authorizations',
    groupLabel: 'Authorizations',
    open: true,
    categoryMatchers: ['Missing Prior Authorization'],
  },
  { id: 'done', label: 'Done', open: false, categoryMatchers: [] },
];

const OPEN_TASK_GROUP_IDS: readonly TaskGroupId[] = [
  'halt',
  'denial',
  'rejection',
  'records',
  'patient',
  'auth',
];

export const TASK_GROUP_LABEL: Record<TaskGroupId, string> = {
  halt: 'Halts',
  denial: 'Denials',
  rejection: 'Rejections',
  records: 'Records',
  patient: 'Patient info',
  auth: 'Authorizations',
  other: 'Other types',
};

/**
 * Which known type a task belongs to, or null when its category is unrecognised.
 *
 * Null is a real answer, not a failure: Enter's catalog is open-ended and a
 * practice can carry categories Platform has never heard of. Those must stay
 * visible under "Other types" rather than disappearing from the worklist.
 */
export function resolveTaskTypeFromCategoryNames(categoryNames: string[]): TaskTypeId | null {
  const openTypes = TASK_TYPE_DEFINITIONS.filter(t => t.id !== 'all' && t.id !== 'done');
  for (const def of openTypes) {
    if (categoryNames.some(name => def.categoryMatchers.some(m => categoryMatches(name, m)))) {
      return def.id;
    }
  }
  return null;
}

/** Batch group for the All-types view — unknown categories go to 'other'. */
export function resolveTaskGroupId(categoryNames: string[]): TaskGroupId {
  const typeId = resolveTaskTypeFromCategoryNames(categoryNames);
  if (typeId && OPEN_TASK_GROUP_IDS.includes(typeId as TaskGroupId)) {
    return typeId as TaskGroupId;
  }
  return 'other';
}

/**
 * Does this task belong under the given type chip?
 *
 * 'all' matches every OPEN task and 'done' every closed one — neither filters
 * by category, which is why they cannot go through the matcher path.
 */
export function taskMatchesType(
  task: { open: boolean; categoryNames?: string[] },
  typeId: TaskTypeId
): boolean {
  if (typeId === 'all') return task.open;
  if (typeId === 'done') return !task.open;
  const def = TASK_TYPE_DEFINITIONS.find(t => t.id === typeId);
  if (!def) return false;
  return (task.categoryNames ?? []).some(name =>
    def.categoryMatchers.some(m => categoryMatches(name, m))
  );
}
